import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import * as React from "react";

import { cn } from "../../utils/cn";

import { textVariants } from "./text";

function TooltipProvider({
    delay = 0,
    ...props
}: TooltipPrimitive.Provider.Props): React.ReactElement {
    return (
        <TooltipPrimitive.Provider
            data-slot="tooltip-provider"
            delay={delay}
            {...props}
        />
    );
}

function Tooltip({
    ...props
}: TooltipPrimitive.Root.Props): React.ReactElement {
    return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({
    ...props
}: TooltipPrimitive.Trigger.Props): React.ReactElement {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

/**
 * `side` defaults to "top" when not provided. All base-ui sides are
 * supported: "top" | "bottom" | "left" | "right" | "inline-start" |
 * "inline-end" — the slide-in animation classes below cover each.
 *
 * `maxWidth` caps the wrap point for the tooltip body — the popup is
 * always `w-fit` (sizes to content) and `maxWidth` only kicks in when
 * the content would otherwise exceed it. Defaults to `xs` (matches the
 * pre-prop hardcoded behavior). The accepted values map to Tailwind's
 * default `max-w-*` t-shirt scale, so the name signals exactly what it
 * controls rather than a fake "size" abstraction.
 *
 * Surface uses `surface-tint-raised` (olive-500) with paper foreground;
 * sharp `rounded-xs` corners match the rest of the design system.
 * The arrow inherits the same surface so the visual continues into
 * the popup.
 */
type TooltipMaxWidth = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";

// Literal class strings so Tailwind JIT picks them up at build time —
// `max-w-${value}` template strings would not be detected.
const MAX_WIDTH_CLASS: Record<TooltipMaxWidth, string> = {
    xs: "max-w-xs",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl"
};

function TooltipContent({
    className,
    side = "top",
    sideOffset = 4,
    align = "center",
    alignOffset = 0,
    maxWidth = "xs",
    children,
    ...props
}: TooltipPrimitive.Popup.Props &
    Pick<
        TooltipPrimitive.Positioner.Props,
        "align" | "alignOffset" | "side" | "sideOffset"
    > & { maxWidth?: TooltipMaxWidth }): React.ReactElement {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Positioner
                align={align}
                alignOffset={alignOffset}
                side={side}
                sideOffset={sideOffset}
                className="isolate z-50"
            >
                <TooltipPrimitive.Popup
                    data-slot="tooltip-content"
                    className={cn(
                        textVariants({ variant: "small" }),
                        "z-50 inline-flex w-fit origin-(--transform-origin) items-center gap-1.5 rounded-xs bg-surface-tint-raised px-2.5 py-1.5 text-foreground-inverse",
                        MAX_WIDTH_CLASS[maxWidth],
                        "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
                        "data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
                        "data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2",
                        "data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95",
                        "data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95",
                        "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
                        className
                    )}
                    {...props}
                >
                    {children}
                    <TooltipPrimitive.Arrow className="z-50 size-2 rotate-45 bg-surface-tint-raised data-[side=bottom]:top-0 data-[side=top]:-bottom-1 data-[side=left]:top-1/2 data-[side=left]:-right-1 data-[side=left]:-translate-y-1/2 data-[side=right]:top-1/2 data-[side=right]:-left-1 data-[side=right]:-translate-y-1/2 data-[side=inline-end]:top-1/2 data-[side=inline-end]:-left-1 data-[side=inline-end]:-translate-y-1/2 data-[side=inline-start]:top-1/2 data-[side=inline-start]:-right-1 data-[side=inline-start]:-translate-y-1/2" />
                </TooltipPrimitive.Popup>
            </TooltipPrimitive.Positioner>
        </TooltipPrimitive.Portal>
    );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
